// src/components/Pages/ResellBlacklistPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Container,
    Title,
    Text,
    useMantineTheme,
    useMantineColorScheme,
    Paper,
    Group,
    Table,
    Select,
    Loader,
    Center,
    Button,
    Badge,
    SegmentedControl,
    Pagination,
    Tooltip,
    TextInput,
    Modal,
    Menu,
    ActionIcon
} from '@mantine/core';
import {
    IconBan,
    IconWorld,
    IconLanguage,
    IconRadar2,
    IconArrowBackUp,
    IconRefresh,
    IconFlask,
    IconHash,
    IconChevronDown,
    IconLayersLinked,
    IconBrain
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { getFirestore, collection, query, where, getDocs, onSnapshot, getCountFromServer } from 'firebase/firestore';
import { useBackgroundImage } from '../../context/BackgroundImageContext';
import { useAuth } from '../../context/AuthContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';

// --- Utils ---
const CMS_EDIT_URL_BASE = 'https://controlcentre.loveincorporated.com/msnglobal/Content/Gallery/Edit/';

const toDateString = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

function ResellBlacklistPage() {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { isBackgroundImageVisible } = useBackgroundImage();
    const headerTextColor = isBackgroundImageVisible ? theme.white : undefined;

// --- Auth & Navigation ---
    // ARCHITECTURAL FIX: Accessing userProfile object directly for robust role checking
    const { currentUser, userProfile } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // --- State ---
    const [activeTab, setActiveTab] = useState(() => (
        searchParams.get('tab') === 'update' ? 'update' : 'veto'
    )); // 'veto' or 'update'

    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        setSearchParams({ tab: newTab }, { replace: true });
    };

    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [usersMap, setUsersMap] = useState({});

    // --- Filters ---
    const [region, setRegion] = useState('US');
    const [language, setLanguage] = useState('EN');
    const [brand, setBrand] = useState('All');

    // --- Search (New) ---
    const [searchOriginalId, setSearchOriginalId] = useState('');
    const [searchLastFeedId, setSearchLastFeedId] = useState('');

    // --- Pagination ---
    const [activePage, setPage] = useState(1);
    const ROWS_PER_PAGE = 50;

    // --- Loading State ---
    const [processingId, setProcessingId] = useState(null);
    const [processingFamilyOriginalId, setProcessingFamilyOriginalId] = useState(null);

    // NEW: Modal State
    const [isFamilyModalOpen, setIsFamilyModalOpen] = useState(false);
    const [familyModalData, setFamilyModalData] = useState({ item: null, count: 0, isLoading: false });

    // NEW: Single Item Modal State
    const [isSingleModalOpen, setIsSingleModalOpen] = useState(false);
    const [singleModalItem, setSingleModalItem] = useState(null);

    // --- 1. Fetch Users Map ---
    useEffect(() => {
        const db = getFirestore();
        const unsub = onSnapshot(collection(db, 'users'), (snapshot) => {
            const map = {};
            snapshot.forEach((d) => {
                const u = d.data();
                if (u.firstName) map[d.id] = `${u.firstName} ${u.lastName || ''}`;
                else map[d.id] = 'Unknown User';
            });
            setUsersMap(map);
        });
        return () => unsub();
    }, []);

    // --- 2. Fetch Data ---
    const fetchItems = async () => {
        setIsLoading(true);
        setItems([]);
        const db = getFirestore();
        const collectionName = activeTab === 'veto' ? 'resell_veto_log' : 'resell_update_queue';

        try {
            const constraints = [
                where('region', '==', region),
                where('language', '==', language)
            ];
            if (brand !== 'All') {
                constraints.push(where('Brand', '==', brand));
            }

            const q = query(collection(db, collectionName), ...constraints);
            const snapshot = await getDocs(q);
            const loaded = [];
            snapshot.forEach(doc => loaded.push({ id: doc.id, ...doc.data() }));

            // Client-side sort by date (descending)
            const dateField = activeTab === 'veto' ? 'vetoedAt' : 'updateRequestedAt';
            loaded.sort((a, b) => {
                const dateA = a[dateField]?.toDate ? a[dateField].toDate() : new Date(0);
                const dateB = b[dateField]?.toDate ? b[dateField].toDate() : new Date(0);
                return dateB - dateA;
            });

            setItems(loaded);
        } catch (err) {
            console.error("Fetch error:", err);
            notifications.show({ title: 'Error', message: 'Could not fetch logs.', color: 'red' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
        setPage(1);
    }, [activeTab, region, language, brand]);

    // NEW: Reset to page 1 when typing a search query to avoid empty pagination states
    useEffect(() => {
        setPage(1);
    }, [searchOriginalId, searchLastFeedId]);

    // --- 3.1 Actions ---

    // --- NEW: Open Single Item Modal ---
    const openSingleModal = (item) => {
        setSingleModalItem(item);
        setIsSingleModalOpen(true);
    };

    // --- NEW: Execute Single Item Reinstate (Called from Modal) ---
    const confirmReinstateSingle = async () => {
        const item = singleModalItem;
        if (!currentUser || !item) return;

        setProcessingId(item.id);

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch('https://us-central1-freelancer-ideas-portal.cloudfunctions.net/reinstateItem', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    docId: item.id,
                    sourceType: activeTab
                })
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.message || 'Failed');
            }

            notifications.show({ title: 'Restored', message: 'Item returned to the Resell Radar.', color: 'green' });
            setItems(prev => prev.filter(i => i.id !== item.id));
            setIsSingleModalOpen(false); // Close the modal on success

        } catch (err) {
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
        } finally {
            setProcessingId(null);
        }
    };

    // --- 3.2. Actions Part II ---

    // --- NEW: Open Modal & Fetch Global Count ---
    const openFamilyModal = async (item) => {
        setIsFamilyModalOpen(true);
        setFamilyModalData({ item, count: 0, isLoading: true });

        try {
            const db = getFirestore();
            const collectionName = activeTab === 'veto' ? 'resell_veto_log' : 'resell_update_queue';
            // Query for the specific originalId across the entire target collection
            const q = query(collection(db, collectionName), where('originalId', '==', item.originalId));
            const snapshot = await getCountFromServer(q);

            setFamilyModalData({ item, count: snapshot.data().count, isLoading: false });
        } catch (err) {
            console.error("Failed to fetch family count:", err);
            notifications.show({ title: 'Error', message: 'Could not fetch family count.', color: 'red' });
            setIsFamilyModalOpen(false);
        }
    };

    // --- NEW: Execute Family Reinstate (Called from Modal) ---
    const confirmReinstateFamily = async () => {
        const item = familyModalData.item;
        if (!currentUser || !item?.originalId) return;

        setProcessingFamilyOriginalId(item.originalId);

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch('https://us-central1-freelancer-ideas-portal.cloudfunctions.net/reinstateFamily', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    originalId: item.originalId,
                    sourceType: activeTab
                })
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.message || 'Failed');
            }

            notifications.show({ title: 'Family Restored', message: `All ${item.originalId} variants returned to the Resell Radar.`, color: 'green' });

            // Optimistic UI update locally
            setItems(prev => prev.filter(i => i.originalId !== item.originalId));
            setIsFamilyModalOpen(false); // Close modal on success

        } catch (err) {
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
        } finally {
            setProcessingFamilyOriginalId(null);
        }
    };


    // --- 4. Filtering & Pagination ---
    const filteredItems = useMemo(() => {
        let result = items;

        if (searchOriginalId.trim()) {
            const term = searchOriginalId.toLowerCase().trim();
            result = result.filter(item =>
                item.originalId && String(item.originalId).toLowerCase().includes(term)
            );
        }

        if (searchLastFeedId.trim()) {
            const term = searchLastFeedId.toLowerCase().trim();
            result = result.filter(item =>
                item.lastFeedId && String(item.lastFeedId).toLowerCase().includes(term)
            );
        }

        return result;
    }, [items, searchOriginalId, searchLastFeedId]);

    const totalPages = Math.ceil(filteredItems.length / ROWS_PER_PAGE);

    const paginatedItems = useMemo(() => {
        const start = (activePage - 1) * ROWS_PER_PAGE;
        const end = start + ROWS_PER_PAGE;
        return filteredItems.slice(start, end);
    }, [filteredItems, activePage]);

    // --- Render ---
    return (
        <Box style={{
            width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column',
            backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
            backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent',
            backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'scroll',
        }}>

            {/* --- TOP SECTION: Fixed Layout independent of table --- */}
            <Box py="xl">
                <Container size="md"> {/* Fixed width container for stability */}
                    <Title order={1} ta="center" mb="xs" style={{ color: headerTextColor }}>
                        <IconBan size={32} color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]} style={{ marginRight: theme.spacing.sm, verticalAlign: 'middle' }} />
                        Resell Blacklist
                    </Title>
                    <Text size="lg" ta="center" mb="xl" style={{ color: headerTextColor }}>
                        Review vetoed items and update requests.
                        Reinstate if necessary.
                    </Text>

                    <Paper withBorder shadow="sm" p="md" radius="md">
                        <Group justify="space-between" mb="md">
                            <SegmentedControl
                                value={activeTab}
                                onChange={handleTabChange}
                                data={[
                                    { label: 'Veto Log', value: 'veto' },
                                    { label: 'Update Queue', value: 'update' }
                                ]}
                                color="blue"
                                size="md"
                            />
                            <Button variant="subtle" size="xs" onClick={fetchItems} leftSection={<IconRefresh size={14} />}>
                                Refresh
                            </Button>
                        </Group>

                        <Group grow>
                            <Select
                                label="Region"
                                leftSection={<IconWorld size={16} />}
                                data={['UK', 'US', 'AE', 'AU', 'CA', 'DE', 'ES', 'FR', 'ZA']}
                                value={region}
                                onChange={(v) => setRegion(v || 'US')}
                            />
                            <Select
                                label="Language"
                                leftSection={<IconLanguage size={16} />}
                                data={['EN', 'DE', 'ES', 'FR']}
                                value={language}
                                onChange={(v) => setLanguage(v || 'EN')}
                            />
                            <Select
                                label="Brand"
                                leftSection={<IconRadar2 size={16} />}
                                data={['All', 'loveMONEY', 'loveFOOD', 'loveEXPLORING', 'lovePROPERTY']}
                                value={brand}
                                onChange={(v) => setBrand(v || 'All')}
                            />
                        </Group>
                        {/* NEW: Search Inputs Row */}
                        <Group grow mt="md">
                            <TextInput
                                label="Search Original ID"
                                placeholder="E.g. 123456"
                                leftSection={<IconHash size={16} />}
                                value={searchOriginalId}
                                onChange={(e) => setSearchOriginalId(e.currentTarget.value)}
                                clearable
                            />
                            <TextInput
                                label="Search Last Feed ID"
                                placeholder="E.g. 987654"
                                leftSection={<IconHash size={16} />}
                                value={searchLastFeedId}
                                onChange={(e) => setSearchLastFeedId(e.currentTarget.value)}
                                clearable
                            />
                        </Group>
                    </Paper>
                </Container>
            </Box>

            {/* --- BOTTOM SECTION: Fluid Table --- */}
            <Container fluid px="xl" pb="xl" style={{ flexGrow: 1 }}>
                <Paper withBorder shadow="sm" radius="md" p="md">
                    {isLoading ? (
                        <Center p="xl"><Loader /><Text ml="md">Loading Logs...</Text></Center>
                    ) : items.length === 0 ? (
                        <Center p="xl"><Text c="dimmed">No items found in this list.</Text></Center>
                    ) : (
                        <>
                            <Table.ScrollContainer minWidth={1000}>
                                <Table striped highlightOnHover verticalSpacing="sm">
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th w={160}>Added On</Table.Th>
                                            <Table.Th>Title (Latest)</Table.Th>
                                            <Table.Th style={{ textAlign: 'center' }}>Brand</Table.Th>
                                            <Table.Th style={{ textAlign: 'center' }}>Original ID</Table.Th>
                                            <Table.Th style={{ textAlign: 'center' }}>Last Variant</Table.Th>
                                            {activeTab === 'veto' && <Table.Th>Veto Reason</Table.Th>}
                                            <Table.Th style={{ textAlign: 'center' }}>Action</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {paginatedItems.map((item) => (
                                            <Table.Tr key={item.id}>
                                                <Table.Td>
                                                    <Text size="sm">
                                                        {toDateString(activeTab === 'veto' ? item.vetoedAt : item.updateRequestedAt)}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td>
                                                    <Text size="sm" fw={500} lineClamp={2} title={item.latestTitle}>
                                                        {item.latestTitle}
                                                    </Text>
                                                </Table.Td>
                                                <Table.Td style={{ textAlign: 'center' }}><Badge variant="outline" color="gray">{item.Brand}</Badge></Table.Td>
                                                <Table.Td style={{ textAlign: 'center' }}>
                                                    <Text size="xs" c="dimmed">{item.originalId}</Text>
                                                </Table.Td>

                                                {/* Last Variant / CMS Link */}
                                                <Table.Td style={{ textAlign: 'center' }}>
                                                    {item.lastFeedId ? (
                                                        <Tooltip label={`Edit Latest Variant: ${item.lastFeedId}`} withArrow>
                                                            <Button
                                                                component="a"
                                                                href={`${CMS_EDIT_URL_BASE}${item.lastFeedId}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                size="compact-xs"
                                                                variant="outline"
                                                                style={{ minWidth: 70, justifyContent: 'center' }}
                                                            >
                                                                {item.lastFeedId}
                                                            </Button>
                                                        </Tooltip>
                                                    ) : (
                                                        <Text size="xs" c="dimmed">-</Text>
                                                    )}
                                                </Table.Td>

                                                {activeTab === 'veto' && (
                                                    <Table.Td>
                                                        <Text size="sm" fs="italic" style={{ maxWidth: 300, fontSize: '0.9em' }}>
                                                            {item.vetoReason}
                                                        </Text>
                                                    </Table.Td>
                                                )}

                                                <Table.Td style={{ textAlign: 'right' }}>
                                                    <Group gap="xs" justify="flex-end">

                                                        {/* Refactor Button - Update Queue Only (Admin/Mgmt) - PLACED TO RIGHT */}
                                                        {activeTab === 'update' && ['editor', 'admin', 'management'].includes(userProfile?.role) &&
                                                            ['221309', '390367', '82332', '93061', '98871', '92669', '255871', '296409', '307282', '526882', '547926', '549422', '73391', '88969'].includes(String(item.originalId)) && (
                                                                <Tooltip label="Open Epistemic Audit" withArrow>
                                                                    <Button
                                                                        size="compact-xs"
                                                                        color="violet"
                                                                        variant="filled"
                                                                        leftSection={<IconBrain size={14} />}
                                                                        onClick={() => navigate(`/lab/epistemic?id=${item.originalId}`)}
                                                                    >
                                                                        Check Facts
                                                                    </Button>
                                                                </Tooltip>
                                                            )}
                                                            {/* Split Button Implementation */}
                                                        <Group wrap="nowrap" gap={0}>
                                                            <Button
                                                                size="compact-xs"
                                                                color="orange"
                                                                variant="light"
                                                                leftSection={<IconArrowBackUp size={14} />}
                                                                loading={processingId === item.id}
                                                                onClick={() => openSingleModal(item)} // <-- Update this line
                                                                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: '1px solid var(--mantine-color-orange-outline)' }}
                                                            >
                                                                Return Item to Radar
                                                            </Button>

                                                            <Menu transitionProps={{ transition: 'pop' }} position="bottom-end" withinPortal>
                                                                <Menu.Target>
                                                                    <ActionIcon
                                                                        variant="light"
                                                                        color="red"
                                                                        size={24}
                                                                        loading={processingFamilyOriginalId === item.originalId}
                                                                        style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                                                                    >
                                                                        <IconChevronDown size={14} />
                                                                    </ActionIcon>
                                                                </Menu.Target>
                                                                <Menu.Dropdown>
                                                                    <Menu.Item
                                                                        leftSection={<IconLayersLinked size={14} />}
                                                                        color="red"
                                                                        onClick={() => openFamilyModal(item)}
                                                                    // Optional: If you truly want the entire row's background tinted permanently 
                                                                    // (not just on hover), you can uncomment the line below. 
                                                                    // style={{ backgroundColor: 'var(--mantine-color-gray-light)' }}
                                                                    >
                                                                        <Text
                                                                            size="xs" // Options: 'xs', 'sm', 'md', 'lg', or a specific value like '11px'
                                                                            fw={600}  // Font weight (e.g., 500 for medium, 600 for semi-bold)
                                                                        >
                                                                            Return Entire Family (Canonical: {item.originalId})
                                                                        </Text>
                                                                    </Menu.Item>
                                                                </Menu.Dropdown>
                                                            </Menu>
                                                        </Group>

                                                        

                                                        

                                                    </Group>
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            </Table.ScrollContainer>

                            {totalPages > 1 && (
                                <Group justify="center" mt="xl">
                                    <Pagination total={totalPages} value={activePage} onChange={setPage} />
                                </Group>
                            )}
                        </>
                    )}
                </Paper>
            </Container>
            {/* --- SINGLE ITEM CONFIRMATION MODAL --- */}
            <Modal
                opened={isSingleModalOpen}
                onClose={() => setIsSingleModalOpen(false)}
                title="Confirm Item Return"
                centered
            >
                <Text size="sm" mb="md">
                    Are you sure you want to return this specific variant (Original ID: <strong>{singleModalItem?.originalId}</strong>
                    {singleModalItem?.lastFeedId && <>, Variant ID: <strong>{singleModalItem.lastFeedId}</strong></>}
                    ) to the Resell Radar?
                </Text>
                <Text size="sm" c="dimmed" mb="xl">
                    This will permanently remove it from the current {activeTab === 'veto' ? 'Veto Log' : 'Update Queue'}. Other regional variants of this article will not be affected.
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setIsSingleModalOpen(false)}>Cancel</Button>
                    <Button
                        color="orange"
                        loading={processingId === singleModalItem?.id}
                        onClick={confirmReinstateSingle}
                    >
                        Confirm Return
                    </Button>
                </Group>
            </Modal>
            {/* --- CONFIRMATION MODAL --- */}
            <Modal
                opened={isFamilyModalOpen}
                onClose={() => setIsFamilyModalOpen(false)}
                title="Confirm Family Return"
                centered
            >
                {familyModalData.isLoading ? (
                    <Center p="xl"><Loader type="dots" /><Text ml="md">Calculating global family variants...</Text></Center>
                ) : (
                    <>
                        <Text size="sm" mb="md">
                            You are about to return <strong>{familyModalData.count} variant(s)</strong> of Original ID <strong>{familyModalData.item?.originalId}</strong> (across all regions and languages) to the Resell Radar.
                        </Text>
                        <Text size="sm" c="dimmed" mb="xl">
                            This action will permanently remove them from the current {activeTab === 'veto' ? 'Veto Log' : 'Update Queue'}.
                        </Text>
                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setIsFamilyModalOpen(false)}>Cancel</Button>
                            <Button
                                color="red"
                                loading={processingFamilyOriginalId === familyModalData.item?.originalId}
                                onClick={confirmReinstateFamily}
                            >
                                Confirm Return
                            </Button>
                        </Group>
                    </>
                )}
            </Modal>
        </Box>
    );
}

export default ResellBlacklistPage;